import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { contactSnapshot } from '@/lib/contactSnapshot'
import { useFeature, useLimitCheck } from '@/lib/billing'
import { Link2, UserPlus, X } from 'lucide-react'
import { toast } from 'sonner'

/**
 * LinkContactModal — binds an UNBOUND invoice (sentinel or NULL contact_id)
 * to a real contact. Pick an existing contact OR quick-create one inline,
 * then write the FK + refresh the snapshot fields via contactSnapshot.
 *
 * Scope: only ever opened for unbound invoices (the caller gates on
 * !hasValidContact). Independent of isLocked — repairing a legacy unbound
 * row must work even on paid/void invoices. The page's invoices UPDATE is
 * covered by the existing workspace_isolation RLS policy (no new policy).
 *
 * Mirrors MakeRecurringModal's overlay shape + the Invoices.tsx inline
 * quick-create (gates, insert, ['contacts'] invalidation).
 */
export default function LinkContactModal({
  invoiceId, workspaceId, onClose, onLinked, onPick,
}: {
  workspaceId: string
  onClose: () => void
  // Default ("bind to invoice") mode — used by InvoiceDetail. When onPick is
  // omitted, the modal updates invoices.contact_id + the snapshot directly and
  // calls onLinked. invoiceId + onLinked are required together in this mode.
  invoiceId?: string
  onLinked?: () => void
  // Caller-driven mode — used by RecurringInvoiceDetail. When provided, the
  // modal hands the chosen contact to onPick (instead of writing to invoices)
  // and then closes. The caller decides what to persist (e.g. stage the pick
  // into form state for a deferred save).
  onPick?: (contact: { id: string; first_name: string; last_name?: string | null; email?: string | null; company_name?: string | null }) => void | Promise<void>
}) {
  const queryClient = useQueryClient()

  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts', 'link-contact', workspaceId],
    queryFn: async () => {
      const { data } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, email, company_name')
        .eq('workspace_id', workspaceId)
        .order('first_name')
      return data || []
    },
    enabled: !!workspaceId,
  })

  // Contacts feature + limit gates — same soft UI gates as NewInvoiceModal's
  // quick-create. The DB-side RLS + plan limits remain the final backstop.
  const hasContactsFeature = useFeature('contacts')
  const { atLimit, limit, isLoading: limitLoading } = useLimitCheck('contacts', contacts.length)
  const canQuickCreate = hasContactsFeature && !atLimit && !limitLoading

  const [selectedId, setSelectedId] = useState('')
  const [linking, setLinking] = useState(false)

  // ─── Inline quick-create customer state (lifted from Invoices.tsx) ────────
  const [showQuickCreate, setShowQuickCreate] = useState(false)
  const [qcForm, setQcForm] = useState({ first_name: '', last_name: '', email: '', company_name: '' })
  const [qcSaving, setQcSaving] = useState(false)
  const qcSet = (k: keyof typeof qcForm, v: string) => setQcForm(p => ({ ...p, [k]: v }))

  const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }

  // Resolve the chosen contact. Two modes:
  //  - onPick provided (caller-driven): hand the contact to the caller and
  //    close. The caller persists it however it likes (e.g. stage into form
  //    state for a deferred save). The modal writes nothing itself.
  //  - onPick omitted (default): bind directly onto the invoice — write the
  //    FK + refresh the snapshot via the canonical helper (keeps FK and
  //    displayed name in sync). company_id is left as-is.
  const bindContact = async (contact: { id: string; first_name: string; last_name?: string | null; email?: string | null; company_name?: string | null }) => {
    setLinking(true)
    try {
      if (onPick) {
        await onPick(contact)
        onClose()
        return
      }
      const snap = contactSnapshot(contact)
      const { error } = await supabase
        .from('invoices')
        .update({
          contact_id: contact.id,
          contact_name: snap.contact_name,
          company_name: snap.company_name,
          contact_email: contact.email || null,
        })
        .eq('id', invoiceId!)
      if (error) { toast.error(`Couldn't link customer: ${error.message}`); return }
      toast.success('Customer linked')
      onLinked!()
    } finally {
      setLinking(false)
    }
  }

  const handleLinkExisting = async () => {
    const contact = contacts.find(c => c.id === selectedId)
    if (!contact) { toast.error('Please select a customer'); return }
    await bindContact(contact)
  }

  const handleQuickCreate = async () => {
    if (!qcForm.first_name.trim()) { toast.error('First name is required'); return }
    setQcSaving(true)
    const { data, error } = await supabase
      .from('contacts')
      .insert({
        workspace_id: workspaceId,
        first_name: qcForm.first_name.trim(),
        last_name:  qcForm.last_name.trim()  || null,
        email:      qcForm.email.trim()      || null,
        company_name: qcForm.company_name.trim() || null,
      })
      .select('id, first_name, last_name, email, company_name')
      .single()
    setQcSaving(false)
    if (error) { toast.error(`Couldn't add customer: ${error.message}`); return }
    // Broad invalidation so every contact list across the app picks up the
    // new contact (prefix-match on ['contacts', …]).
    queryClient.invalidateQueries({ queryKey: ['contacts'] })
    // Bind the freshly-created contact straight onto the invoice — the
    // operator's intent is "link this new person", so skip the re-pick step.
    await bindContact(data!)
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 20, padding: 28, width: '100%', maxWidth: 480, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Link2 style={{ width: 16, height: 16, color: 'var(--accent)' }} />
            <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>Link customer</h2>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 18, height: 18 }} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto', minHeight: 0 }}>
          <div style={{ padding: '10px 12px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 9, fontSize: 12, color: 'var(--ink-secondary)' }}>
            This invoice isn’t linked to a saved contact. Pick an existing customer or add a new one to bind it — this enables recurring billing and keeps the customer in sync.
          </div>

          <div>
            <label style={lbl}>Customer *</label>
            <select value={selectedId} onChange={e => setSelectedId(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
              <option value="">Select a customer…</option>
              {contacts.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name || ''} {c.company_name ? `— ${c.company_name}` : ''}</option>)}
            </select>

            {contacts.length === 0 && (
              <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '6px 0 0' }}>
                {!hasContactsFeature
                  ? 'Contacts aren’t included on your plan. Upgrade to add customers.'
                  : atLimit
                    ? `You’ve reached your plan’s contact limit${limit != null ? ` (${limit})` : ''}. Upgrade to add more.`
                    : 'No customers yet. Add one below.'}
              </p>
            )}

            {canQuickCreate && !showQuickCreate && (
              <button type="button" onClick={() => setShowQuickCreate(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 8, padding: '4px 0', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-text)', fontSize: 12, fontWeight: 600 }}>
                <UserPlus style={{ width: 13, height: 13 }} /> Add new customer
              </button>
            )}
            {!canQuickCreate && contacts.length > 0 && (
              <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '6px 0 0' }}>
                {!hasContactsFeature
                  ? 'Quick-add unavailable on your plan.'
                  : `Contact limit reached${limit != null ? ` (${limit})` : ''}. Upgrade to add more.`}
              </p>
            )}
          </div>

          {/* Inline quick-create panel — minimal form, mirrors NewInvoiceModal. */}
          {showQuickCreate && (
            <div style={{ padding: '14px 14px 16px', background: 'var(--surface)', border: '1px solid var(--accent-border)', borderRadius: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <UserPlus style={{ width: 14, height: 14, color: 'var(--accent)' }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-primary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>New customer</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div>
                  <label style={lbl}>First name *</label>
                  <input value={qcForm.first_name} onChange={e => qcSet('first_name', e.target.value)}
                    placeholder="Alex" style={inp} autoFocus
                    onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'}
                    onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
                </div>
                <div>
                  <label style={lbl}>Last name</label>
                  <input value={qcForm.last_name} onChange={e => qcSet('last_name', e.target.value)}
                    placeholder="Johnson" style={inp}
                    onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'}
                    onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
                </div>
              </div>
              <div>
                <label style={lbl}>Email</label>
                <input type="email" value={qcForm.email} onChange={e => qcSet('email', e.target.value)}
                  placeholder="alex@company.com" style={inp}
                  onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'}
                  onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
              </div>
              <div>
                <label style={lbl}>Company</label>
                <input value={qcForm.company_name} onChange={e => qcSet('company_name', e.target.value)}
                  placeholder="Acme Inc." style={inp}
                  onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'}
                  onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button type="button"
                  onClick={() => { setShowQuickCreate(false); setQcForm({ first_name:'', last_name:'', email:'', company_name:'' }) }}
                  className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>
                  Cancel
                </button>
                <button type="button" onClick={handleQuickCreate} disabled={qcSaving || linking}
                  className="btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
                  {qcSaving ? 'Adding…' : 'Add & link'}
                </button>
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 20, flexShrink: 0 }}>
          <button onClick={onClose} className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
          <button onClick={handleLinkExisting} disabled={!selectedId || linking} className="btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
            {linking ? 'Linking…' : 'Link customer'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}
